import { type IEvent } from 'clients/PlatformApi';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import LocaleUtil from 'utils/LocaleUtil';
import { SKILL_CHIP_CLASS } from '../../renderers/skillMatches';
import AiFindingsSection, { aiFindingsStyles } from '../AiFindingsSection/AiFindingsSection';

interface AgentFinding {
  agentName: string;
  agentEventId: string;
  riskReason: string;
  riskScore: string | null;
  empName: string | null;
  empId: string | null;
  transTime: string | null;
  transactionId: string;
  detectedAt: string;
  skillMatches?: string[];
}

interface AgentFindingsProps {
  eventColor: string;
  event: IEvent;
  limitedView?: boolean;
}

const SkillChips: React.FC<{ skills: string[] }> = ({ skills }) => (
  <>
    {skills.map((skill) => (
      <span key={skill} className={SKILL_CHIP_CLASS}>
        {skill}
      </span>
    ))}
  </>
);

const FindingDetail: React.FC<{ finding: AgentFinding; locale: any }> = ({ finding, locale }) => (
  <div>
    <p>
      <strong>{finding.agentName}</strong>
      {finding.riskScore &&
        ` (${LocaleUtil.getLocalizedStr({ string: locale.event.lpFindingsRisk, replacements: { score: finding.riskScore } })})`}
    </p>
    <p>{finding.riskReason}</p>
    {finding.skillMatches && finding.skillMatches.length > 0 && (
      <div
        style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}
      >
        <span className={aiFindingsStyles.label}>
          {locale.event.lpFindingsSkills ?? 'Skills: '}
        </span>
        <SkillChips skills={finding.skillMatches} />
      </div>
    )}
    {finding.empName && (
      <p>
        <span className={aiFindingsStyles.label}>{locale.event.lpFindingsEmployee}</span>
        {finding.empName}
        {finding.empId && ` (${finding.empId})`}
      </p>
    )}
    {finding.transTime && (
      <p>
        <span className={aiFindingsStyles.label}>{locale.event.lpFindingsTransactionTime}</span>
        {finding.transTime}
      </p>
    )}
    <p>
      <a
        href={`/events?eventId=${finding.agentEventId}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'inherit', textDecoration: 'underline' }}
      >
        {locale.event.lpFindingsViewAgentEvent}
      </a>
    </p>
  </div>
);

const LimitedFindingDetail: React.FC<{ finding: AgentFinding; locale: any }> = ({
  finding,
  locale,
}) => {
  if (!finding.skillMatches?.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
      <span className={aiFindingsStyles.label}>{locale.event.lpFindingsSkills ?? 'Skills: '}</span>
      <SkillChips skills={finding.skillMatches} />
    </div>
  );
};

const AgentFindings: React.FC<AgentFindingsProps> = ({ eventColor, event, limitedView }) => {
  const locale = useLocale();

  const findings: AgentFinding[] =
    (event.details?.agentFindings as AgentFinding[] | undefined) ?? [];

  if (findings.length === 0) return null;

  const title = LocaleUtil.getLocalizedStr({
    string: locale.event.lpFindingsTitle,
    replacements: { count: findings.length },
  });

  const summaryContent = findings.map((finding) => {
    if (limitedView) {
      return finding.skillMatches?.length ? (
        <div
          key={finding.agentEventId}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '2px 0' }}
        >
          <SkillChips skills={finding.skillMatches} />
        </div>
      ) : null;
    }
    const truncated =
      finding.riskReason.length > 80 ? finding.riskReason.slice(0, 80) + '...' : finding.riskReason;
    return (
      <p key={finding.agentEventId}>
        <strong>{finding.agentName}:</strong> {truncated}
      </p>
    );
  });

  const expandedContent = findings.map((finding, idx) => (
    <React.Fragment key={finding.agentEventId}>
      {idx > 0 && <div className={aiFindingsStyles.divider} />}
      {limitedView ? (
        <LimitedFindingDetail finding={finding} locale={locale} />
      ) : (
        <FindingDetail finding={finding} locale={locale} />
      )}
    </React.Fragment>
  ));

  return (
    <AiFindingsSection
      eventColor={eventColor}
      title={title}
      tooltip={locale.event.viewAgentFindings}
      summaryContent={summaryContent}
      expandedContent={expandedContent}
    />
  );
};

export default AgentFindings;
